/**
 * AuthCallback — where the email confirmation link lands.
 *
 * signUpWithEmail sets emailRedirectTo to `${origin}/auth/callback`, but no such
 * route existed: `/auth/*` matched it and redirected to the sign-in form, and a
 * react-router redirect keeps neither the query string nor the hash. The `?code=`
 * Supabase appends was therefore discarded on arrival, and the user was returned
 * to a sign-in form that said nothing about why.
 *
 * The exchange itself is not done here. The client is built with
 * `detectSessionInUrl: true` and `flowType: "pkce"`, so supabase-js reads the URL
 * and exchanges the code on its own; what it needed was for the URL to still be
 * there when it looked. Once the session lands, AuthContext's onAuthStateChange
 * fires and App navigates away from every /auth path on its own.
 *
 * So this page's whole job is to hold the route and to be honest about the two
 * ways it ends badly: Supabase reporting the link invalid or expired, and
 * nothing happening at all.
 *
 * @module pages/auth/AuthCallback
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { YDMWordmark } from '@/components/brand';

/** How long to wait for supabase-js before saying so. One network round trip. */
const EXCHANGE_TIMEOUT_MS = 12_000;

/**
 * Supabase reports a bad link two ways depending on the flow — `?error=` for
 * PKCE, `#error=` for the implicit one. Read both: guessing wrong here shows a
 * spinner until the timeout instead of the reason, which is the failure this
 * page exists to prevent.
 */
export const readCallbackError = (search: string, hash: string): string | null => {
  const fromSearch = new URLSearchParams(search);
  const fromHash = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

  for (const params of [fromSearch, fromHash]) {
    const description = params.get('error_description');
    const code = params.get('error');
    if (description) return description.replace(/\+/g, ' ');
    if (code) return code.replace(/[_+]/g, ' ');
  }
  return null;
};

const AuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const linkError = readCallbackError(location.search, location.hash);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (linkError) return;
    const timer = setTimeout(() => setTimedOut(true), EXCHANGE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [linkError]);

  // A successful exchange never renders a result here: the session arrives,
  // AuthContext picks it up, and App redirects out of /auth before this page
  // has anything to say about it.
  const failure = linkError
    ?? (timedOut ? t('auth.linkUnusable') : null);

  return (
    <div className="min-h-screen bg-primary-500 flex flex-col items-center justify-center p-4 text-ink-950">
      <div className="max-w-md w-full text-center">
        <YDMWordmark variant="hero" bg="yellow" size="lg" />

        {failure ? (
          <div
            role="alert"
            className="mt-8 rounded-xl bg-white p-6 shadow-lg"
          >
            <AlertCircle className="mx-auto h-8 w-8 text-warning-700" aria-hidden="true" />
            <h1 className="mt-3 text-lg font-bold text-ink-900">{t('auth.confirmFailed')}</h1>
            <p className="mt-2 text-sm text-neutral-700">{failure}</p>
            <p className="mt-1 text-xs text-neutral-700">
              {t('auth.linkOneUse')}
            </p>
            <Link
              to="/auth/signin"
              className="mt-4 inline-block rounded-lg bg-ink-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-900"
            >
              {t('auth.backToSignIn')}
            </Link>
          </div>
        ) : (
          <div className="mt-8 flex items-center justify-center gap-2 text-sm font-medium text-ink-900">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{t('auth.confirming')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
